import { useContext, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — full-width "mega-menu" header navigation.
 *
 * Owns the whole interactive nav: a row of top-level entries plus a single
 * full-bleed dropdown panel that shows the hovered entry's columns. Hover to
 * open (with a small close grace delay, like a real mega-menu), click to
 * toggle on touch, click-outside / Escape to close.
 *
 * Unlike `NavDropdown` (one small panel per entry), the panel here is shared
 * and spans the header width — so it must be anchored to a positioned
 * ancestor. The primitive root is `display: contents` (it adds no box, so the
 * `<nav>` keeps participating in the header flex row); the panel is
 * `position: absolute; left/right: 0` and resolves against the nearest
 * positioned ancestor. **The parent header must be `relative`** and tall
 * enough for `panelTop` (default `76px`, the header row height).
 *
 * YAML usage:
 *   - type: MegaMenu
 *     items: ~~.navItems            # [{ label, url?, seeAllLabel?, seeAllUrl?,
 *                                   #    columns?: [{ title?, items: [{label,url?}] }] }]
 *     preventNavigation: true       # demo/editor mode — anchors don't navigate
 *     panelTop: '76px'              # inline top offset of the panel
 *     navClass / itemClass / activeClass / panelClass /
 *     columnTitleClass / itemLinkClass / seeAllClass  # full class overrides
 *
 * Class overrides fully replace the defaults (pass the complete list). The
 * defaults match the Académie française marine theme.
 */
const SANS = "'Inter', 'Inter Fallback', system-ui, sans-serif"

const DEFAULT_NAV = 'hidden @5xl:flex items-center gap-[18px]'
const DEFAULT_ITEM =
  'relative flex items-center gap-1 h-[76px] text-[#F5F0E8] hover:opacity-70 transition-opacity cursor-pointer'
const DEFAULT_ACTIVE = 'absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9A961]'
const DEFAULT_PANEL =
  'hidden @5xl:block absolute left-0 right-0 z-40 bg-[#1A2744] border-t border-[#F5F0E8]/10'
const DEFAULT_COLUMN_TITLE = 'text-[#C9A961] uppercase tracking-widest mb-4'
const DEFAULT_ITEM_LINK =
  'block text-[#F5F0E8] hover:text-[#C4B49A] transition-colors opacity-85'
const DEFAULT_SEEALL =
  'inline-flex items-center gap-2 text-[#C9A961] hover:opacity-70 transition-opacity'

export const MegaMenu = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const rawItems = resolve(props.items)
  const items = Array.isArray(rawItems) ? rawItems : []
  const preventNavigation = resolve(props.preventNavigation) === true
  const panelTop = resolve(props.panelTop) ?? '76px'

  const navClass = resolve(props.navClass) ?? DEFAULT_NAV
  const itemClass = resolve(props.itemClass) ?? DEFAULT_ITEM
  const activeClass = resolve(props.activeClass) ?? DEFAULT_ACTIVE
  const panelClass = resolve(props.panelClass) ?? DEFAULT_PANEL
  const columnTitleClass = resolve(props.columnTitleClass) ?? DEFAULT_COLUMN_TITLE
  const itemLinkClass = resolve(props.itemLinkClass) ?? DEFAULT_ITEM_LINK
  const seeAllClass = resolve(props.seeAllClass) ?? DEFAULT_SEEALL

  const [openLabel, setOpenLabel] = useState(null)
  const ref = useRef(null)
  const closeTimer = useRef(null)

  useEffect(() => {
    if (openLabel == null) return
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenLabel(null)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenLabel(null)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onKey)
    }
  }, [openLabel])

  // Clear any pending close timer on unmount.
  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const hasColumns = (item) => Array.isArray(item?.columns) && item.columns.length > 0

  const openNow = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpenLabel(label)
  }
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpenLabel(null), 120)
  }
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }

  const navigate = (e, url) => {
    if (preventNavigation || !url || url === '#') e.preventDefault()
    setOpenLabel(null)
  }

  const activeItem = items.find((it) => it?.label === openLabel)

  return (
    <ActionDependant {...props}>
      <div ref={ref} style={{ display: 'contents' }}>
        <nav className={navClass} onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          {items.map((item, idx) => {
            const cols = hasColumns(item)
            const isOpen = openLabel === item?.label
            return (
              <button
                key={`${item?.label}-${idx}`}
                type="button"
                className={itemClass}
                style={{ fontFamily: SANS, fontSize: '14px', letterSpacing: '0.01em' }}
                onMouseEnter={() => (cols ? openNow(item.label) : cancelClose())}
                onClick={(e) => {
                  if (cols) {
                    setOpenLabel(isOpen ? null : item.label)
                  } else {
                    navigate(e, item?.url)
                  }
                }}
              >
                {item?.label}
                {cols && (
                  <ChevronDown
                    size={12}
                    className={`opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  />
                )}
                {isOpen && <span className={activeClass} />}
              </button>
            )
          })}
        </nav>

        {activeItem && hasColumns(activeItem) && (
          <div
            className={panelClass}
            style={{ top: panelTop }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className="max-w-[1440px] mx-auto" style={{ padding: '40px 40px 44px' }}>
              <div
                className="grid gap-0"
                style={{ gridTemplateColumns: `repeat(${activeItem.columns.length}, 1fr)` }}
              >
                {activeItem.columns.map((col, ci) => (
                  <div
                    key={ci}
                    style={{
                      paddingRight: ci < activeItem.columns.length - 1 ? '40px' : 0,
                      paddingLeft: ci > 0 ? '40px' : 0,
                      borderRight:
                        ci < activeItem.columns.length - 1
                          ? '1px solid rgba(245,240,232,0.08)'
                          : 'none',
                    }}
                  >
                    {col?.title && (
                      <p
                        className={columnTitleClass}
                        style={{ fontFamily: SANS, fontSize: '11px', letterSpacing: '0.2em' }}
                      >
                        {col.title}
                      </p>
                    )}
                    <ul>
                      {(Array.isArray(col?.items) ? col.items : []).map((child, ii) => {
                        const childLabel = typeof child === 'string' ? child : child?.label
                        const childUrl = typeof child === 'string' ? '#' : child?.url ?? '#'
                        return (
                          <li key={`${childLabel}-${ii}`}>
                            <a
                              href={childUrl}
                              className={itemLinkClass}
                              style={{ fontFamily: SANS, fontSize: '15px', lineHeight: 1.5, padding: '5px 0' }}
                              onClick={(e) => navigate(e, childUrl)}
                            >
                              {childLabel}
                            </a>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
              </div>
              {(activeItem.seeAllLabel || activeItem.seeAllUrl) && (
                <div className="mt-8 pt-5" style={{ borderTop: '1px solid rgba(245,240,232,0.08)' }}>
                  <a
                    href={activeItem.seeAllUrl ?? '#'}
                    className={seeAllClass}
                    style={{ fontFamily: SANS, fontSize: '13px', letterSpacing: '0.06em' }}
                    onClick={(e) => navigate(e, activeItem.seeAllUrl)}
                  >
                    {activeItem.seeAllLabel ?? `Voir tout — ${activeItem.label} →`}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
